import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Typography, Button, CircularProgress, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, Fade, IconButton, Chip } from '@mui/material';
import { Edit as EditIcon} from '@mui/icons-material';
import WorkHistoryIcon from '@mui/icons-material/WorkHistory';
import BancoModal from './BancoModal'; 
import MigrationModal from './MigrationModal'; 

import administradorDbService from '../../services/administradorDbService';

interface Banco {
  id: number;
  nome: string;
  host: string;
  database: string;
  username: string;
  data_expiracao: string;
}

const BancoList: React.FC = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [bancos, setBancos] = useState<Banco[]>([]);
  const [modalOpen, setModalOpen] = useState<boolean>(false);
  const [selectedBanco, setSelectedBanco] = useState<Banco | null>(null);
  const [migrationModalOpen, setMigrationModalOpen] = useState<boolean>(false);
  const [selectedMigrationBanco, setSelectedMigrationBanco] = useState<string | null>(null);

  useEffect(() => {
    const fetchBancos = async () => {
      try {
        setLoading(true);
        const response = await administradorDbService.getAllBancos();
        setBancos(response);
        setLoading(false);
      } catch (error) {
        setError('Não foi possível carregar os bancos.');
        setLoading(false);
      }
    };

    fetchBancos();
  }, []);

  const handleOpenModal = (banco?: Banco) => {
    setSelectedBanco(banco || null); 
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setSelectedBanco(null);
  };

  const isExpired = (dataExpiracao: string) => {
    const today = new Date();
    const expirationDate = new Date(dataExpiracao);
    return expirationDate < today;
  };

  const handleSubmitModal = async (formData: Banco) => {
    try {
      if (formData.id === 0) {
        await administradorDbService.createBanco(formData);
      } else {
        await administradorDbService.updateBanco(formData.id, formData);
      }
      handleCloseModal();
      setLoading(true);
      const response = await administradorDbService.getAllBancos();
      setBancos(response);
      setLoading(false);
    } catch (error) {
      setError('Erro ao salvar o banco.');
    }
  };

  const handleOpenMigrationModal = (banco: Banco) => {
    setSelectedMigrationBanco(banco.database); 
    setMigrationModalOpen(true);
  };

  const handleCloseMigrationModal = () => {
    setMigrationModalOpen(false);
    setSelectedMigrationBanco(null);
  };
  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '70vh' }}>
        <CircularProgress size={60} thickness={4} sx={{ color: 'primary.main' }} />
      </Box>
    );
  }

  return (
    <Fade in={true} timeout={800}>
      <div>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 4 }}>
          <Box>
            <Typography variant="h4" component="h1" sx={{ fontWeight: 'bold', mb: 1 }}>
              Bancos
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Gerenciamento de bancos do sistema
            </Typography>
          </Box>
          <Button variant="contained" onClick={() => handleOpenModal()}>
            Adicionar Banco
          </Button>
        </Box>

        {error && <Typography color="error">{error}</Typography>}

        <TableContainer component={Paper}>
          <Table sx={{ minWidth: 650 }} aria-label="Tabela de Bancos">
            <TableHead>
              <TableRow>
                <TableCell>Subdominio</TableCell>
                <TableCell>Host</TableCell>
                <TableCell>Banco</TableCell>
                <TableCell>Usuário</TableCell>
                <TableCell>Data Expiração</TableCell>
                <TableCell>Ações</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {bancos.map((banco) => (
                <TableRow key={banco.id}>
                  <TableCell>{banco.nome}</TableCell>
                  <TableCell>{banco.host}</TableCell>
                  <TableCell>{banco.database}</TableCell>
                  <TableCell>{banco.username}</TableCell>
                  <TableCell>
                    <Chip
                      label={banco.data_expiracao}
                      color={isExpired(banco.data_expiracao) ? 'error' : 'success'}
                      sx={{ width: 'auto', maxWidth: 120, marginRight: 1 }}
                    />
                    {isExpired(banco.data_expiracao) && (
                      <Typography variant="caption" color="error" sx={{ display: 'block' }}>
                        Expirado
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <IconButton onClick={() => handleOpenModal(banco)}>
                      <EditIcon />
                    </IconButton>
                    <IconButton onClick={() => handleOpenMigrationModal(banco)}>
                      <WorkHistoryIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Modal para editar ou adicionar banco */}
        <BancoModal
          open={modalOpen}
          onClose={handleCloseModal}
          onSubmit={handleSubmitModal}
          banco={selectedBanco}
        />
         <MigrationModal
            open={migrationModalOpen}
            onClose={handleCloseMigrationModal}
            banco={selectedMigrationBanco}
          />
      </div>
    </Fade>
  );
};

export default BancoList;
